import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { useDesignarCertificadoServico } from '@/hooks/useCertificadosServico';

interface DesignarServicoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const onlyDigits = (v: string) => v.replace(/\D/g, '');

export function DesignarServicoDialog({ open, onOpenChange }: DesignarServicoDialogProps) {
  const { showSuccess, showError } = useNotification();
  const designar = useDesignarCertificadoServico();

  const [cpf, setCpf] = useState('');
  const [rotulo, setRotulo] = useState('');
  const [observacao, setObservacao] = useState('');

  const cpfDigits = onlyDigits(cpf);
  const podeEnviar = cpfDigits.length === 11 && !designar.isPending;

  const reset = () => {
    setCpf('');
    setRotulo('');
    setObservacao('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeEnviar) return;
    designar.mutate(
      { cpf: cpfDigits, rotulo: rotulo || null, observacao: observacao || null },
      {
        onSuccess: () => {
          showSuccess('Certificado de serviço designado.');
          reset();
          onOpenChange(false);
        },
        onError: (err) => notifyMutationError(err, showError),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Designar certificado de serviço</DialogTitle>
          <DialogDescription>
            Informe o CPF de um titular que já possui certificado ativo e conexão com a PDPJ no
            sistema. Ele passa a servir consultas públicas de usuários sem certificado próprio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="servico-cpf">CPF do titular</Label>
            <Input
              id="servico-cpf"
              inputMode="numeric"
              placeholder="Somente números"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="servico-rotulo">Rótulo (opcional)</Label>
            <Input
              id="servico-rotulo"
              placeholder="Ex.: Certificado institucional"
              value={rotulo}
              onChange={(e) => setRotulo(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="servico-obs">Observação (opcional)</Label>
            <Input
              id="servico-obs"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!podeEnviar}>
              {designar.isPending ? (
                <Spinner size={16} className="text-inherit mr-1.5" />
              ) : null}
              Designar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
